import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Download, TrendingUp, Users, DollarSign, ArrowLeft } from "lucide-react";

const fmtMXN = (n) => `$${Math.round(n).toLocaleString("es-MX")}`;

export default function RevenueCalculator() {
  // Inputs
  const [providers, setProviders] = useState(889);
  const [pctEsencial, setPctEsencial] = useState(70);
  const [pctCompleto, setPctCompleto] = useState(25);
  const [recertPct, setRecertPct] = useState(40);
  const [onboardingFee, setOnboardingFee] = useState(80000);
  const [years, setYears] = useState(3);
  const [pctPremium, setPctPremium] = useState(5);

  // Prices (editable)
  const [priceEsencial, setPriceEsencial] = useState(800);
  const [priceCompleto, setPriceCompleto] = useState(1400);
  const [pricePremium, setPricePremium] = useState(2200);
  const [recertPrice, setRecertPrice] = useState(150);

  const totalPct = pctEsencial + pctCompleto + pctPremium;
  const pctValid = totalPct === 100;

  const calc = useMemo(() => {
    const nEsencial = Math.round(providers * (pctEsencial / 100));
    const nCompleto = Math.round(providers * (pctCompleto / 100));
    const nPremium = Math.round(providers * (pctPremium / 100));
    const revEsencial = nEsencial * priceEsencial;
    const revCompleto = nCompleto * priceCompleto;
    const revPremium = nPremium * pricePremium;
    const revRecert = Math.round(providers * (recertPct / 100)) * recertPrice * 3;
    const subAnual = revEsencial + revCompleto + revPremium + revRecert;
    const year1 = subAnual + onboardingFee;
    const totalYears = Array.from({ length: years }, (_, i) => {
      const growth = Math.pow(1.08, i);
      const yr = i === 0 ? year1 : Math.round(subAnual * growth);
      return { year: i + 1, total: yr };
    });
    const totalContract = totalYears.reduce((a, b) => a + b.total, 0);
    // External comparison: ~$2,500 per supplier (paid by supplier)
    const externalEquivalent = providers * 2500;
    return {
      nEsencial, nCompleto, nPremium,
      revEsencial, revCompleto, revPremium, revRecert,
      subAnual, year1, totalYears, totalContract,
      externalEquivalent,
    };
  }, [providers, pctEsencial, pctCompleto, pctPremium, priceEsencial,
      priceCompleto, pricePremium, recertPct, recertPrice, onboardingFee, years]);

  const exportData = () => {
    const csv = [
      ["Concepto", "Cantidad", "Precio MXN", "Total MXN"],
      ["Esencial", calc.nEsencial, priceEsencial, calc.revEsencial],
      ["Completo", calc.nCompleto, priceCompleto, calc.revCompleto],
      ["Premium (transferible)", calc.nPremium, pricePremium, calc.revPremium],
      ["Re-certificación trimestral", `${Math.round(providers * recertPct / 100)} × 3`, recertPrice, calc.revRecert],
      ["Onboarding (one-time año 1)", 1, onboardingFee, onboardingFee],
      ["Subtotal anual recurring", "", "", calc.subAnual],
      ["Total año 1 (con onboarding)", "", "", calc.year1],
      ...calc.totalYears.map(y => [`Año ${y.year}`, "", "", y.total]),
      ["TOTAL CONTRATO", years, "", calc.totalContract],
    ].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `calculadora-lala-elite-${providers}-proveedores.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin/configuracion" className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Volver a Configuración
      </Link>

      <div className="mt-2 mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">simulador comercial</div>
        <h1 className="font-display text-4xl">Calculadora de revenue</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
          Simula escenarios de ingresos cambiando volumen, mix de planes, recertificación y duración del contrato.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-2 space-y-5">
          <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="calc-inputs-volume">
            <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2"><Users size={18} /> Volumen</h2>
            <Field label="Proveedores en padrón" testid="input-providers">
              <input type="number" value={providers} onChange={(e) => setProviders(Math.max(0, Number(e.target.value)))} className="input-field w-full" />
            </Field>
            <Field label={`Duración del contrato: ${years} año(s)`}>
              <input type="range" min="1" max="5" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-full accent-[var(--lala-blue)]" />
            </Field>
          </div>

          <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="calc-inputs-mix">
            <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2"><TrendingUp size={18} /> Mix de planes</h2>
            <Slider label={`Esencial: ${pctEsencial}%`} value={pctEsencial} onChange={setPctEsencial} testid="slider-esencial" />
            <Slider label={`Completo: ${pctCompleto}%`} value={pctCompleto} onChange={setPctCompleto} testid="slider-completo" />
            <Slider label={`Premium (transferible): ${pctPremium}%`} value={pctPremium} onChange={setPctPremium} testid="slider-premium" />
            <div className={`mt-3 text-sm font-semibold ${pctValid ? "text-green-700" : "text-red-700"}`} data-testid="total-pct">
              Total: {totalPct}% {pctValid ? "✓" : "(ajustar a 100%)"}
            </div>
            <Slider label={`Re-certificación trimestral (% que la activa): ${recertPct}%`} value={recertPct} onChange={setRecertPct} testid="slider-recert" />
          </div>

          <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="calc-inputs-prices">
            <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2"><DollarSign size={18} /> Precios MXN</h2>
            <Field label="Esencial">
              <input type="number" value={priceEsencial} onChange={(e) => setPriceEsencial(Number(e.target.value))} className="input-field w-full" />
            </Field>
            <Field label="Completo">
              <input type="number" value={priceCompleto} onChange={(e) => setPriceCompleto(Number(e.target.value))} className="input-field w-full" />
            </Field>
            <Field label="Premium">
              <input type="number" value={pricePremium} onChange={(e) => setPricePremium(Number(e.target.value))} className="input-field w-full" />
            </Field>
            <Field label="Re-certificación trimestral / proveedor">
              <input type="number" value={recertPrice} onChange={(e) => setRecertPrice(Number(e.target.value))} className="input-field w-full" />
            </Field>
            <Field label="Onboarding masivo (one-time año 1)">
              <input type="number" value={onboardingFee} onChange={(e) => setOnboardingFee(Number(e.target.value))} className="input-field w-full" />
            </Field>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 space-y-5">
          <div className="sketch-border bg-[var(--lala-blue)] text-[var(--cream)] p-6" data-testid="calc-summary">
            <div className="font-hand text-xl opacity-90">resultado</div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <div className="text-xs uppercase tracking-wider opacity-70">Subtotal anual</div>
                <div className="font-display text-3xl" data-testid="result-anual">{fmtMXN(calc.subAnual)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider opacity-70">Año 1 (con setup)</div>
                <div className="font-display text-3xl" data-testid="result-year1">{fmtMXN(calc.year1)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider opacity-70">Total {years} año(s)</div>
                <div className="font-display text-3xl" data-testid="result-contract">{fmtMXN(calc.totalContract)}</div>
              </div>
            </div>
          </div>

          <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="calc-breakdown">
            <h2 className="font-display text-2xl mb-3">Desglose anual</h2>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]">
                <tr><th className="py-2">Concepto</th><th>Volumen</th><th>Precio</th><th className="text-right">Total</th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                <Row label="Esencial" qty={calc.nEsencial} price={priceEsencial} total={calc.revEsencial} testid="row-esencial" />
                <Row label="Completo" qty={calc.nCompleto} price={priceCompleto} total={calc.revCompleto} testid="row-completo" />
                <Row label="Premium (transferible)" qty={calc.nPremium} price={pricePremium} total={calc.revPremium} testid="row-premium" />
                <Row label="Recertificación trimestral" qty={`${Math.round(providers * recertPct / 100)} × 3`} price={recertPrice} total={calc.revRecert} testid="row-recert" />
                <Row label="Onboarding (one-time)" qty="1" price={onboardingFee} total={onboardingFee} testid="row-onboarding" />
              </tbody>
            </table>
          </div>

          <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="calc-years">
            <h2 className="font-display text-2xl mb-3">Proyección {years} año(s)</h2>
            <div className="space-y-2">
              {calc.totalYears.map(y => (
                <div key={y.year} className="flex items-center justify-between" data-testid={`year-${y.year}`}>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="font-semibold w-16">Año {y.year}</span>
                    <div className="flex-1 bg-[var(--cream)] rounded-full h-3 overflow-hidden border border-[var(--line)]">
                      <div className="h-full bg-[var(--lala-blue)]" style={{ width: `${Math.min(100, (y.total / calc.year1) * 80)}%` }} />
                    </div>
                  </div>
                  <div className="font-display text-xl text-[var(--lala-blue)] ml-3">{fmtMXN(y.total)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="sketch-border bg-[var(--cream)] p-5 text-sm text-[var(--ink-soft)]" data-testid="calc-comparison">
            <div className="font-display text-lg text-[var(--ink)] mb-2">Comparación con modelo externo</div>
            Si {providers} proveedores pagaran ~$2,500 cada uno al certificador externo:
            <div className="font-display text-2xl text-[var(--ink)] mt-1">{fmtMXN(calc.externalEquivalent)} / año</div>
            <div className="mt-2">
              Lala Elite paga <b>{fmtMXN(calc.year1)}</b> en año 1 y entrega significativamente más valor (plataforma, sello, directorio, datos propios).
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={exportData} data-testid="export-csv-btn" className="btn-primary inline-flex items-center gap-2">
              <Download size={16} /> Exportar CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block mb-3" data-testid={testid}>
      <span className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Slider({ label, value, onChange, testid }) {
  return (
    <label className="block mb-3" data-testid={testid}>
      <span className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</span>
      <input type="range" min="0" max="100" value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[var(--lala-blue)]" />
    </label>
  );
}

function Row({ label, qty, price, total, testid }) {
  return (
    <tr data-testid={testid}>
      <td className="py-2 font-medium">{label}</td>
      <td>{qty}</td>
      <td>{typeof price === "number" ? `$${price.toLocaleString("es-MX")}` : price}</td>
      <td className="text-right font-display text-[var(--lala-blue)]">{`$${Number(total || 0).toLocaleString("es-MX")}`}</td>
    </tr>
  );
}
